import React from 'react';
import { Link } from 'react-router-dom';
import { AuditAlert, AlertSeverity, AlertType } from '../../types/api';

interface AlertsOverviewProps {
  alerts: AuditAlert[];
}

const AlertsOverview: React.FC<AlertsOverviewProps> = ({ alerts }) => {
  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return 'bg-red-100 text-red-800 border-red-200';
      case AlertSeverity.ERROR:
        return 'bg-red-50 text-red-700 border-red-200';
      case AlertSeverity.WARNING:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case AlertSeverity.INFO:
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return '🚨';
      case AlertSeverity.ERROR:
        return '❌';
      case AlertSeverity.WARNING:
        return '⚠️';
      case AlertSeverity.INFO:
        return 'ℹ️';
      default:
        return '📋';
    }
  };

  const getTypeLabel = (type: AlertType) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const unresolvedAlerts = alerts.filter(alert => !alert.resolved);
  const criticalAlerts = unresolvedAlerts.filter(alert => alert.severity === AlertSeverity.CRITICAL);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-medium text-gray-900">Recent Alerts</h3>
            {unresolvedAlerts.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {unresolvedAlerts.length} unresolved
              </span>
            )}
            {criticalAlerts.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-200 text-red-900">
                {criticalAlerts.length} critical
              </span>
            )}
          </div>
          <Link
            to="/alerts"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            View all →
          </Link>
        </div>
      </div>
      
      <div className="divide-y divide-gray-200">
        {alerts.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="text-green-400 mb-2">
              <svg className="mx-auto h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm">No recent alerts</p>
            <p className="text-xs text-gray-400">System is running smoothly</p>
          </div>
        ) : (
          alerts.slice(0, 5).map((alert) => (
            <div key={alert.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <span className="text-lg">{getSeverityIcon(alert.severity)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                    {!alert.resolved && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {alert.title}
                  </p>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {alert.description}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      {getTypeLabel(alert.type)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getTimeAgo(alert.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {alerts.length > 5 && (
        <div className="px-6 py-3 bg-gray-50 text-center">
          <Link
            to="/alerts"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            View {alerts.length - 5} more alerts
          </Link>
        </div>
      )}
    </div>
  );
};

export default AlertsOverview; 